import { EpsonEncoder } from "@/tools/QzTray/epson.encoder";
import { defineStore } from "pinia";
import * as qz from 'qz-tray';
import { toast } from "vue3-toastify";
import * as Sentry from "@sentry/browser";
import { Order } from "@/interfaces/Order";
import { ITicket } from "@/interfaces/ITicket";
import { useEventStore } from "./EventStore";
import { useOrderStore } from "./OrderStore";
import { GlobalHelpers } from "@/helpers/global.helper";
import { AppPrice } from "@/tools/Price";
import shoetest from 'shoetest';
import { KEYUTIL, KJUR, hextorstr, stob64 } from 'jsrsasign';
import privateKey from '@/tools/QzTray/keys/privatekey';
import digitalCertificate from '@/tools/QzTray/keys/certificate';
import axios from "@/tools/Axios/axios";
import { Buffer } from "buffer";
import QRCode from 'qrcode';

export enum PrintModeEnum {
    NONE = 'none',
    A4 = 'a4',
    A4_AUTO = 'a4_auto',
    OANDB_EPSON = 'oandb_epson'
}

export enum QZPrinterENUM {
    A4_AUTO = 'a4_auto',
    OANDB_EPSON = 'oandb_epson'
}

export type thermalTicketType = {
    title: string
    address?:string|false
    ticket: {
        number: string
        date: string
        ticketType: string
        addons?: string[]
        code: string
    }
    totalPrice: string
    seat?: string
}

export type thermalCashOutType = {
    sessionNumber: string
    organisation: string
    event: string
    operator: string
    openedAt: string
    closedAt: string
    sales: {
        orders: number
        tickets: number
        amount: string
    }
    ticketTypes: {
        label: string
        quantity: number
        amount: string
    }[]
    paymentMethods: {
        label: string
        amount: string
    }[]
    totals: {
        receipts: string
        refunds: string
        balance: string
    }
}

const thermalTicketDataTest: thermalTicketType = {
    title: '[[ TICKET TEST ]]',
    address: '34 rue du Languedoc, 31000 Toulouse',
    ticket: {
        date: GlobalHelpers.formatDate(new Date()),
        number: "860881116946218",
        ticketType: "Plein tarif",
        addons: [
            'Place de parking'
        ],
        code: '860881116946218'
    },
    totalPrice: '54.00'
}

/**
 * Niveaux de qualite d'impression (DPI).
 * - DRAFT (150) : rapide, encre economisee
 * - NORMAL (300) : bon compromis, defaut
 * - HIGH (600) : maximum qualite, plus lent
 */
export enum PrintQuality {
    DRAFT = 150,
    NORMAL = 300,
    HIGH = 600,
}

export interface PrintState {
    connectedPrinter: string|null,
    printerMode: PrintModeEnum|null
    qzConfig: any
    // Impression couleur (true) ou noir et blanc (false).
    // Stockee en localStorage par opérateur.
    colorPrint: boolean
    // Qualite d'impression en DPI (150, 300, 600).
    printQuality: PrintQuality
    // Impression automatique en fin de commande (mode A4 QZ Tray uniquement).
    // Par defaut true pour conserver le comportement historique.
    // Si false : l'operateur doit cliquer manuellement sur "Imprimer" depuis
    // l'ecran de recap commande (SummaryView).
    autoPrint: boolean
}

const COLOR_PRINT_STORAGE_KEY = 'print_color_enabled'
const PRINT_QUALITY_STORAGE_KEY = 'print_quality_dpi'
const AUTO_PRINT_STORAGE_KEY = 'print_auto_enabled'

function loadColorPrint(): boolean {
    try {
        return localStorage.getItem(COLOR_PRINT_STORAGE_KEY) === 'true'
    } catch {
        return false
    }
}

function loadPrintQuality(): PrintQuality {
    try {
        const raw = localStorage.getItem(PRINT_QUALITY_STORAGE_KEY)
        const value = raw ? Number(raw) : NaN
        if (value === PrintQuality.DRAFT || value === PrintQuality.HIGH) {
            return value
        }
        return PrintQuality.NORMAL
    } catch {
        return PrintQuality.NORMAL
    }
}

function loadAutoPrint(): boolean {
    try {
        const raw = localStorage.getItem(AUTO_PRINT_STORAGE_KEY)
        // Default true si rien n'est stocke (comportement historique).
        if (raw === null) return true
        return raw === 'true'
    } catch {
        return true
    }
}

qz.security.setCertificatePromise(function(resolve, reject) {
    resolve(digitalCertificate)
});

qz.security.setSignatureAlgorithm("SHA512");
qz.security.setSignaturePromise(function (toSign) {
    return function(resolve, reject) {
        try {
            var pk = KEYUTIL.getKey(privateKey);
            var sig = new KJUR.crypto.Signature({"alg": "SHA512withRSA"})
            sig.init(pk);
            sig.updateString(toSign);
            var hex = sig.sign();
            resolve(stob64(hextorstr(hex)));
        } catch (err) {
            reject(err);
        }
    }
})

qz.printers.setPrinterCallbacks((evt) => {
    if (evt.eventType == 'PRINTER') {
        if (evt.statusText == 'OFFLINE') {
            toast.error(`Imprimante "${evt.printerName}" déconnecté`)
            usePrintStore().connectedPrinter = null
        }
        if (evt.statusText == 'OK') {
            usePrintStore().connectedPrinter = evt.printerName
        }
    }
});


export const usePrintStore = defineStore("PrintStore", {
    state: (): PrintState => {
        return {
            connectedPrinter: null,
            printerMode: null,
            qzConfig: null,
            colorPrint: loadColorPrint(),
            printQuality: loadPrintQuality(),
            autoPrint: loadAutoPrint(),
        }
    },
    actions: {
        canPrint() {
            if (this.getPrinterMode !== PrintModeEnum.NONE) {
                if (Object.values(QZPrinterENUM).includes(this.getPrinterMode) && !this.connectedPrinter) {
                    return false
                }
                return true
            }
            return false
        },
        async printOrder(order: Order, tickets: ITicket[]) {
            const orderStore = useOrderStore()
            switch (this.getPrinterMode) {
                case PrintModeEnum.OANDB_EPSON:
                    await this.printThermalTickets(order, tickets)
                    break;
                case PrintModeEnum.A4:
                    orderStore.printOrder(order, tickets)
                    break;
                case PrintModeEnum.A4_AUTO:
                    const url = orderStore.getPdfOrderUrl(order, tickets)
                    const { data } = await axios.get(url, {
                        responseType: 'arraybuffer'
                    })
                    const base64Data = Buffer.from(data).toString('base64')
                    await this.printBase64Data(base64Data)
                    break;
            }
        },
        async printTicket(order: Order, ticket: ITicket) {
            const orderStore = useOrderStore()
            switch (this.getPrinterMode) {
                case PrintModeEnum.OANDB_EPSON:
                    await this.printThermalTickets(orderStore.order, [ticket])
                    break;
                case PrintModeEnum.A4:
                    orderStore.printTicket(order, ticket)
                    break;
                case PrintModeEnum.A4_AUTO:
                    const url = orderStore.getPdfTicketUrl(orderStore.order, ticket)
                    const { data } = await axios.get(url, {
                        responseType: 'arraybuffer'
                    })
                    const base64Data = Buffer.from(data).toString('base64')
                    await this.printBase64Data(base64Data)
                    break;
            }

        },
        setPrinterMode(mode: string) {
            localStorage.setItem('printerMode', mode)
            this.printerMode = mode
        },
        /**
         * Active ou desactive l'impression automatique en fin de commande
         * pour le mode A4 QZ Tray (A4_AUTO). Si false, l'operateur doit
         * cliquer manuellement sur "Imprimer" depuis SummaryView.
         * Persiste le choix en localStorage.
         */
        setAutoPrint(enabled: boolean) {
            this.autoPrint = enabled
            try {
                localStorage.setItem(AUTO_PRINT_STORAGE_KEY, String(enabled))
            } catch {
                // localStorage inaccessible, on continue sans persistance
            }
        },
        /**
         * Indique si l'impression doit se declencher automatiquement en fin
         * de commande. Vrai pour tous les modes sauf A4_AUTO quand le toggle
         * autoPrint est false (le toggle s'applique uniquement a ce mode,
         * cf. cahier des charges).
         */
        shouldAutoPrint(): boolean {
            if (this.getPrinterMode === PrintModeEnum.A4_AUTO) {
                return this.autoPrint
            }
            return true
        },
        /**
         * Active ou desactive l'impression couleur.
         * Persiste le choix en localStorage pour s'appliquer aux prochains
         * prints (les configs QZ sont recreees a chaque print).
         */
        setColorPrint(enabled: boolean) {
            this.colorPrint = enabled
            try {
                localStorage.setItem(COLOR_PRINT_STORAGE_KEY, String(enabled))
            } catch {
                // localStorage inaccessible, on continue sans persistance
            }
            // Mettre a jour la config A4 existante si elle existe deja
            if (this.qzConfig && this.connectedPrinter) {
                this.qzConfig = qz.configs.create(this.connectedPrinter, {
                    encoding: 'Cp850',
                    copies: 1,
                    colorType: enabled ? "color" : "blackwhite",
                    density: this.printQuality,
                    scaleContent: false,
                })
            }
        },
        /**
         * Change la qualite d'impression (DPI).
         * Persiste en localStorage. La nouvelle valeur s'applique aux
         * prochains prints (les configs QZ sont recreees a chaque print).
         */
        setPrintQuality(quality: PrintQuality) {
            this.printQuality = quality
            try {
                localStorage.setItem(PRINT_QUALITY_STORAGE_KEY, String(quality))
            } catch {
                // localStorage inaccessible, on continue sans persistance
            }
            // Met a jour la config A4 active si elle existe
            if (this.qzConfig && this.connectedPrinter) {
                this.qzConfig = qz.configs.create(this.connectedPrinter, {
                    encoding: 'Cp850',
                    copies: 1,
                    colorType: this.colorPrint ? "color" : "blackwhite",
                    density: quality,
                    scaleContent: false,
                })
            }
        },
        async createSocketConnection(): Promise<boolean> {
            if (!qz.websocket.isActive()) {
                try {
                    await qz.websocket.connect()
                } catch (error) {
                    toast.error("Problème lors de la connexion à QZ Tray.")
                    Sentry.captureException(error)
                    return false
                }
            }
            return true
        },
        async connectDefaultPrinter(searchPattern: string = 'EPSON TM'): Promise<boolean> {
            const connected = await this.createSocketConnection()
            if (this.connectedPrinter) {
                return true
            }
            if (connected) {
                try {
                    const found = await qz.printers.getDefault()
                    const isMatch = shoetest.test(searchPattern, found)
                    if (!isMatch) {
                        throw new Error(`Impossible de trouver l'imprimante ${searchPattern}`);
                    }
                    this.connectedPrinter = found
                    this.qzConfig = qz.configs.create(this.connectedPrinter, {
                        encoding: 'Cp850',
                        copies: 1,
                        colorType: this.colorPrint ? "color" : "blackwhite",
                        density: this.printQuality,
                        scaleContent: false
                    })
                    toast(`Imprimante "${found}" connecté`)
                    try {
                        qz.printers.startListening(found).then(() => {
                            qz.printers.getStatus().then((status) => {
                                return true
                            })

                        });
                    } catch (error) {
                        Sentry.captureException(error)
                        toast.error("Impossible de trouver l'imprimante \"EPSON TM...\"")
                        return false
                    }
                    return true
                } catch (error) {
                    Sentry.captureException(error)
                    toast.error("Impossible de trouver l'imprimante \"EPSON TM...\"")
                }
            }
            return false
        },
        async findPrinters(printerType: string = '') {
            const connected = await this.createSocketConnection()
            if (connected) {
                try {
                    const found = await qz.printers.getDefault()
                    if (found) {
                        return [found]
                    }
                } catch (error) {
                    Sentry.captureException(error)
                }
            }
            return false
        },
        async printTestTicket(amount: number = 1) {
            try {
                await this.createSocketConnection()


                for (let index = 0; index < amount; index++) {

                    const qrCode = await QRCode.toDataURL(thermalTicketDataTest.ticket.number, {
                        width: 200,
                        errorCorrectionLevel: 'M',
                        margin: 0
                    })
                    const data = getThermalTicketData(thermalTicketDataTest, qrCode, index + 1, amount)
                    const config = qz.configs.create(this.connectedPrinter, {
                        encoding: 'Cp850', //'Cp1252',
                        copies: 1,
                        colorType: this.colorPrint ? "color" : "blackwhite",
                        density: this.printQuality,
                        scaleContent: false,
                        jobName: `Ticket_${thermalTicketDataTest.ticket.number}_${index}`,
                    })
                    await qz.print(config, data)
                    await new Promise(resolve => setTimeout(resolve, 1000))
                }
            } catch (error) {
                Sentry.captureException(error)
                toast.error("Impossible d'imprimer le ticket de test")
            }
        },
        async printBase64Data(base64string: string) {
            try {
                const connected = await this.connectDefaultPrinter()
                if (connected) {
                    toast('Impression en cours', {icon: "🖨️", autoClose: 2000})
                    const data = [{
                        type: 'pixel',
                        format: 'pdf',
                        flavor: 'base64',
                        data: base64string
                    }];
                    await qz.print(this.qzConfig, data)
                    return true
                }
                throw new Error();
            } catch (error) {
                Sentry.captureException(error)
                toast.error("Problème d'impression")
            }
        },
        async printThermalTickets(order: Order, tickets: ITicket[]) {
            try {
                const connected = await this.connectDefaultPrinter()
                if (!connected) {
                    toast.error("Impossible d'imprimer les tickets de la commande")
                    return false
                }
                const count = tickets.length
                let index = 1

                toast('Impression en cours', {icon: "🖨️", autoClose: 2000})
                for (const ticket of tickets) {

                    const qrCode = await QRCode.toDataURL(ticket.ticketNumber, {
                        width: 200,
                        errorCorrectionLevel: 'M',
                        margin: 0
                    })
                    const data = getThermalTicketData({
                        title: useEventStore().currentEvent.name,
                        address: GlobalHelpers.formatEventAddress(useEventStore().currentEvent),
                        ticket: {
                            date: GlobalHelpers.formatDate(ticket.createdAt),
                            number: ticket.ticketNumber,
                            ticketType: ticket.name,
                            addons: (ticket.addons && ticket.addons.length) ? ticket.addons.map(addon => addon.name): [],
                            code: ticket.ticketNumber,
                        },
                        seat: ticket.seat,
                        totalPrice: new AppPrice(ticket.price.total.sellingPrice).divide100().toString('raw')
                    }, qrCode, index, count)

                    const config = qz.configs.create(this.connectedPrinter, {
                        encoding: 'Cp850', //'Cp1252',
                        copies: 1,
                        colorType: this.colorPrint ? "color" : "blackwhite",
                        density: this.printQuality,
                        scaleContent: false,
                        jobName: `Ticket_${ticket.ticketNumber}_${index}`,
                    })
                    await qz.print(config, data)
                    await new Promise(resolve => setTimeout(resolve, 1000))
                    index++
                }
                return true
            } catch (error) {
                Sentry.captureException(error)
                toast.error("Problème d'impression")
                return false
            }
        },
        async printCashOut(cashOutData: thermalCashOutType) {
            try {
                const connected = await this.connectDefaultPrinter()
                if (!connected) {
                    toast.error("Impossible d'imprimer la sortie de caisse")
                    return false
                }

                toast('Impression en cours', {icon: "🖨️", autoClose: 2000})

                const data = getCashOutThermalData(cashOutData)

                const config = qz.configs.create(this.connectedPrinter, {
                    encoding: 'Cp850',
                    copies: 1,
                    colorType: this.colorPrint ? "color" : "blackwhite",
                    density: this.printQuality,
                    scaleContent: false,
                    jobName: `SortieDeCaisse_${cashOutData.sessionNumber}`,
                })
                await qz.print(config, data)
                toast.success('Sortie de caisse imprimée')
                return true
            } catch (error) {
                Sentry.captureException(error)
                toast.error("Problème d'impression de la sortie de caisse")
                return false
            }
        },
    },
    getters: {
        getPrinterMode(): PrintModeEnum {
            const modeLS = localStorage.getItem('printerMode')
            let mode = this.printerMode
            if (!mode) {
                mode = (modeLS) ? modeLS : PrintModeEnum.NONE
            }
            return mode
        }
    }
})

export const getThermalTicketData = (ticketData: thermalTicketType, qrcode: string, index?: number, total?: number) => {
    const encoder = new EpsonEncoder()
        const data = encoder
            .init()
            .align('center')
            .h1(ticketData.title)
            .newLine()
        if (ticketData.address) {
            data.small(ticketData.address)
        }
        data.stroke()
        if (index && total && total > 1) {
            data.line(`Billet ${index}/${total}`)
        }

            data.bold(`BILLET #${ticketData.ticket.number}`)
            .line(ticketData.ticket.date)
            .stroke()
            .align('left')
            .bold(ticketData.ticket.ticketType)
        if (ticketData.ticket.addons) {
            ticketData.ticket.addons.forEach(addon => {
                data.line(` - ${addon}`)
            })
        }
        data.align('center').stroke()
            .bold(`TOTAL ${ticketData.totalPrice}`)
            .stroke()
            .base64Image(qrcode)
        if (ticketData.seat) {
            data.stroke()
                .underline('Votre place')
                .newLine()
                .doubleSize(ticketData.seat)
                .newLine()
                .stroke()
                .newLine()
        }
        else {
            data.stroke().newLine()
        }

        data.image('/logo.png')
            .small("La premiere solution de billetterie eco-concue")
            .small('https://www.oandb.fr')
            .newLine(5)
            .cut()
        return data.endCode()
}

export const getCashOutThermalData = (cashOutData: thermalCashOutType) => {
    const encoder = new EpsonEncoder()
    const data = encoder
        .init()
        .align('center')
        .h1('SORTIE DE CAISSE')
        .bold(`N°${cashOutData.sessionNumber}`)
        .stroke()
        .align('left')
        .line(`Organisateur : ${cashOutData.organisation}`)
        .line(`Evenement : ${cashOutData.event}`)
        .line(`Operateur : ${cashOutData.operator}`)
        .line(`Ouverte le : ${cashOutData.openedAt}`)
        .line(`Fermee le : ${cashOutData.closedAt}`)
        .stroke()
        .align('center')
        .bold('Synthese des operations')
        .stroke()
        .align('left')

    // Table headers for sales
    data.table(['Commandes', 'Billets', 'Montant'])
    data.table([
        cashOutData.sales.orders.toString(),
        cashOutData.sales.tickets.toString(),
        cashOutData.sales.amount
    ])

    data.stroke()
        .align('center')
        .bold('Detail par tarif')
        .stroke()
        .align('left')

    // Table headers for ticket types
    data.table(['Libelle', 'Qte', 'Montant'])

    // Ticket types
    cashOutData.ticketTypes.forEach(tt => {
        data.table([tt.label, tt.quantity.toString(), tt.amount])
    })

    data.stroke()
        .align('center')
        .bold(`TOTAL ${cashOutData.sales.amount}`)
        .stroke()
        .bold('Recettes')
        .stroke()
        .align('left')

    // Payment methods
    cashOutData.paymentMethods.forEach(pm => {
        data.table([pm.label, pm.amount])
    })

    data.stroke()
        .align('center')
        .bold(`TOTAL ${cashOutData.totals.receipts}`)
        .stroke()
        .bold('Solde de caisse')
        .stroke()
        .align('left')
        .table(['Total recettes', cashOutData.totals.receipts])
        .table(['Total remboursements', cashOutData.totals.refunds])
        .stroke()
        .align('center')
        .doubleSize(`SOLDE: ${cashOutData.totals.balance}`)
        .newLine()
        .stroke()
        .newLine(3)
        .image('/logo.png')
        .small("La premiere solution de billetterie eco-concue")
        .small('https://www.oandb.fr')
        .newLine(5)
        .cut()

    return data.endCode()
}